<template>
  <div class="folder-settings">
    <div class="folder-settings-header d-flex justify-content-between">
      <div class="folder-settings-header__left">
        <eg-button
          class="folder-settings-header__back"
          type="ordinary"
          icon="arrow-left"
          icon-size="extra-medium"
          @click="$router.push(`/workspace/essaygrader/${folderParentId}`)"
        />
        <h1 class="title-folder-settings mb-0">{{ folderName }}</h1>
      </div>
      <div
        class="float-right flex-shrink-0 d-flex align-items-center flex-gap-1"
      >
        <div
          v-b-tooltip.hover="{
            title: disabledEditAndReGradeFolderTooltip,
            disabled: !disabledEditAndReGradeFolderTooltip,
          }"
        >
          <eg-button
            v-if="!features.bulkImports"
            type="secondary"
            :is-loading="rubricLoading"
            :disabled="disabledEditAndReGradeFolder"
            icon="pen-edit"
            icon-size="extra-medium"
            @click="editAndReGradeFolder"
          >
            {{ $t("essays.editRegrade") }}
          </eg-button>
        </div>
        <eg-button
          type="primary"
          icon="metrics-screen"
          icon-size="extra-medium"
          @click="goToMetrics(metricsRoute)"
        >
          {{ $t("common.viewMetrics") }}
        </eg-button>
      </div>
    </div>
    <folder-breadcrumbs
      :folder-parent-id="folderParentId"
      :folder-name="folderName"
      class="mt-2 mb-3"
    />
    <slot></slot>
  </div>
</template>

<script>
import Vue from "vue";
import { isEqual } from "lodash";
import { GRADE_LEVELS } from "@/constants";
import { capitalize } from "@/utils/capitalize";
import { mapGetters } from "vuex";
import {
  getFolder,
  getSingleRubric,
  getSourceMaterial,
  updateFolderGradingDetails,
} from "@/api";
import RubricWithGradingDetailsForm from "@/components/rubrics/rubric_with_grading_details_form/rubric_with_grading_details_form.vue";
import FolderBreadcrumbs from "@/components/folders/folder_breadcrumbs.vue";
import { openRubricPreview } from "@/components/dashboard/components/open_rubric_preview";
import goToMetricsMixin from "@/mixins/goToMetricsMixin.js";

export default {
  name: "FolderSettings",

  components: {
    FolderBreadcrumbs,
  },

  filters: {
    capitalize: function (value) {
      return capitalize(value);
    },
  },

  mixins: [goToMetricsMixin],

  inject: ["workspacesProvider"],

  props: {
    folderParentId: {
      type: [Number, null],
      required: true,
    },
    folderName: {
      type: String,
      required: true,
    },
    gradingDetails: {
      type: Object,
      default: () => {
        return {
          length: "normal",
          intensity: "moderate",
          level: "middleschool",
          customInstructions: null,
          sourceMaterial: null,
          sourceMaterialId: null,
          // { id: ...,  title: .. }
          assignment: null,
          rubricId: null,
          // { id: ...,  name: .. }
          rubric: null,
          inputLanguageId: null,
          // { id: ...,  label: .. }
          inputLanguage: null,
          outputLanguageId: null,
          // { id: ...,  label: .. }
          outputLanguage: null,
        };
      },
    },
  },

  data() {
    return {
      rubricLoading: false,
      queuedEssaysExist: false,
    };
  },

  computed: {
    ...mapGetters(["clerkUserId"]),
    ...mapGetters("languages", ["getLanguagesListMap"]),
    ...mapGetters("config", ["features"]),

    folderId() {
      return +this.$route.params.id;
    },

    metricsRoute() {
      return `/metrics/user/${this.clerkUserId}/class/${this.folder?.parentId}/assignment/${this.folderId}`;
    },

    folder() {
      return this.workspacesProvider.getFolderById(
        this.$route.params.workspaceKey,
        this.folderId
      );
    },

    levelIcon() {
      return GRADE_LEVELS.find(
        (option) => option.value === this.gradingDetails.level
      );
    },
    inputLanguage() {
      return this.getLanguagesListMap[this.gradingDetails.inputLanguageId];
    },

    isMoreThan50Essays() {
      return this.folder?.essaysCount > 50;
    },

    disabledEditAndReGradeFolder() {
      return (
        this.isMoreThan50Essays ||
        !(this.gradingDetails?.rubric?.id || this.gradingDetails?.rubricId) ||
        this.queuedEssaysExist
      );
    },

    disabledEditAndReGradeFolderTooltip() {
      if (this.isMoreThan50Essays) {
        return this.$t("essays.cannotRegradeMoreThan50");
      }

      if (this.queuedEssaysExist) {
        return this.$t("essays.cannotRegradeGradingState");
      }

      return null;
    },
  },

  created() {
    this.$eventBus.$on("queued-essays-changed", this.onQueuedEssaysChanged);
  },

  beforeDestroy() {
    this.$eventBus.$off("queued-essays-changed", this.onQueuedEssaysChanged);
  },

  methods: {
    openRubricPreview,

    onQueuedEssaysChanged(value) {
      this.queuedEssaysExist = value;
    },

    async getCurrentSourceMaterial() {
      if (!this.gradingDetails.sourceMaterialId) {
        return { data: null };
      }

      return getSourceMaterial(this.gradingDetails.sourceMaterialId);
    },

    async editAndReGradeFolder() {
      const folderId = this.folderId;
      const workspaceKey = this.$route.params.workspaceKey;

      this.rubricLoading = true;
      let rubric = null;
      try {
        const { data } = await getSingleRubric(
          this.gradingDetails.rubric?.id || this.gradingDetails.rubricId
        );
        rubric = data;
      } catch (error) {
        this.rubricLoading = false;
        if (error.response?.status !== 404) {
          return; // non-404 already handled by the global error interceptor
        }
        // 404 → rubric was deleted; open the form without a pre-selected rubric
      }
      this.rubricLoading = false;

      let newRubric = rubric;
      let newSourceMaterial = undefined;
      let newGradingDetails = { ...this.gradingDetails };

      const getSourceMaterialId = (sourceMaterial, gradingDetails) => {
        if (sourceMaterial === undefined) {
          return gradingDetails?.sourceMaterialId ?? null;
        }
        return sourceMaterial?.id ?? null;
      };

      const getSignature = (rubric, sourceMaterial, gradingDetails) => ({
        rubricId: rubric?.id ?? null,
        sourceMaterialId: getSourceMaterialId(sourceMaterial, gradingDetails),
        level: gradingDetails?.level ?? null,
        intensity: gradingDetails?.intensity ?? null,
        length: gradingDetails?.length ?? null,
        customInstructions: gradingDetails?.customInstructions || null,
        flexibleScoring: gradingDetails?.flexibleScoring ?? false,
        inputLanguageId: gradingDetails?.inputLanguageId ?? null,
        outputLanguageId: gradingDetails?.outputLanguageId ?? null,
      });

      const initialSignature = getSignature(
        rubric,
        undefined,
        this.gradingDetails
      );
      const formState = Vue.observable({ isDirty: false, isSaving: false });

      this.$openForm("edit-folder-form", {
        forceComponent: RubricWithGradingDetailsForm,
        isModal: true,
        callbackEvent: "no-modal-callback",
        modalProps: {
          size: "lg",
          title: this.$t("essays.regradeTitle", {
            folderName: this.folderName,
          }),
        },
        props: {
          value: {
            selectedRubric: rubric,
            gradingDetails: this.gradingDetails,
          },
          isEdit: true,
        },
        on: {
          input: (newValues) => {
            newRubric = newValues.selectedRubric;
            if (newValues.selectedSourceMaterial !== undefined) {
              newSourceMaterial = newValues.selectedSourceMaterial;
            }
            newGradingDetails = {
              ...newGradingDetails,
              ...newValues.gradingDetails,
            };
            const currentSig = getSignature(
              newRubric,
              newSourceMaterial,
              newGradingDetails
            );
            formState.isDirty = !isEqual(currentSig, initialSignature);
          },
        },
        scopedSlots: {
          "modal-footer": (slotProps) => {
            // Defined here (not in callback) so the save is repeatable after recoverable errors.
            const handleSaveClick = async () => {
              if (formState.isSaving) {
                return;
              }
              if (!newRubric?.id) {
                this.$showToastError(
                  this.$t("essays.pleaseSelectRubricToRegrade")
                );
                return;
              }

              // eslint-disable-next-line no-unused-vars
              const { rubric: _rubric, ...rest } = newGradingDetails;
              const payload = {
                ...rest,
                rubricId: newRubric.id,
                sourceMaterialId: getSourceMaterialId(
                  newSourceMaterial,
                  newGradingDetails
                ),
              };

              formState.isSaving = true;
              try {
                await updateFolderGradingDetails(folderId, {
                  ...payload,
                  regradeExisting: true,
                  replaceExisting: false,
                });
              } catch (saveError) {
                formState.isSaving = false;
                const PLAN_LIMIT_CODES = [
                  "planWordLimitsExceeded",
                  "planEssayLimitsExceeded",
                  "planAdvancedRubricEssaysLimitExceeded",
                  "pausedLimitsExceeded",
                ];
                if (PLAN_LIMIT_CODES.includes(saveError.response?.data?.code)) {
                  return;
                }
                try {
                  await getFolder(folderId, { silent: true });
                } catch (folderError) {
                  if ([403, 404].includes(folderError.response?.status)) {
                    slotProps.close();
                    this.$showToastError(this.$t("essays.folderNotFound"));
                    this.workspacesProvider.removeFolderById(
                      workspaceKey,
                      folderId
                    );
                    if (this.folderParentId) {
                      this.$router.push(
                        `/workspace/${workspaceKey}/${this.folderParentId}`
                      );
                    } else {
                      this.$router.push({ name: "dashboard" });
                    }
                  } else {
                    this.$showToastError(this.$t("errors.saveFailed"));
                  }
                  return;
                }
                this.$showToastError(this.$t("errors.saveFailed"));
                return;
              }

              formState.isSaving = false;
              this.workspacesProvider.updateFolderById(workspaceKey, folderId, {
                gradingDetails: {
                  ...payload,
                  rubric: newRubric,
                  sourceMaterial: newSourceMaterial,
                },
                hasGradingDetails: 1,
              });

              this.$eventBus.$emit("update-essays-table", {
                folderId,
                workspaceKey,
              });

              this.queuedEssaysExist = true;
              slotProps.close();
            };

            return {
              render: (h) =>
                h(
                  "div",
                  {
                    class: "d-flex align-items-center justify-content-end",
                  },
                  [
                    h(
                      "eg-button",
                      {
                        props: {
                          type: "secondary",
                        },
                        on: {
                          click: slotProps.close,
                        },
                      },
                      this.$t("common.close")
                    ),
                    h(
                      "eg-button",
                      {
                        props: {
                          type: "primary",
                          disabled: !formState.isDirty || formState.isSaving,
                          isLoading: formState.isSaving,
                        },
                        on: {
                          click: handleSaveClick,
                        },
                        class: "ml-3",
                      },
                      this.$t("essays.saveRegrade")
                    ),
                  ]
                ),
            };
          },
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.folder-settings-header {
  &__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__back {
    color: var(--color-text-neutral-on-white);
  }
}

.title-folder-settings {
  color: var(--color-text-neutral-on-white, #1c0f30);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-700, 24px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-600, 600);
  line-height: 120%; /* 28.8px */
}

.settings-block {
  gap: 8px;
  max-width: calc(100% / 6 - 16px);

  @media (max-width: 1024px) {
    max-width: calc(100% / 3 - 16px);
    width: calc(100% / 3 - 16px);
  }

  .rubric {
    cursor: pointer;
    text-decoration: underline;
  }
}

.settings-head {
  color: var(--color-text-neutral-secondary, #566370);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-200, 12px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-600, 600);
  line-height: 120%; /* 14.4px */
}

.settings-value {
  color: var(--color-text-neutral-secondary, #566370);
  font-family: var(--typography-font-family-text-primary, Inter);
  font-size: var(--typography-font-size-fs-200, 12px);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-400, 400);
  line-height: 120%; /* 14.4px */
}

.grading-instructions {
  border-radius: var(--radius-rd-400, 12px);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  background: var(--color-surface-neutral-background, #fff);
  gap: 16px;
  padding: 16px;

  @media (max-width: 1024px) {
    flex-wrap: wrap;
  }
}
</style>
